import { useMemo } from 'react';
import { CapTableDetailsModel } from 'api';
import { FULL_ACCESS_VIEWER_VALUE } from 'common/constants/user';
import { CapTableInfo } from 'common/types/capTable';

interface UseCapTableAccessLevelPermissionsParams {
  captableInfo?: CapTableInfo | null;
  currentFirmRole?: number | null;
  currentUserId?: number | null;
}

interface UseCapTableAccessLevelPermissionsReturn {
  canEditAccessLevel: boolean;
}

/**
 * Rules (in priority order):
 * 1. Primary cap tables are always Restricted and immutable — never editable.
 * 2. The creator can always change their own version's access level. Legacy
 *    rows with no creator (created_by == null) have no creator to match, so
 *    this never applies to them — they fall through to rules 3-5 below.
 * 3. FAVs who are not the creator (including on a legacy row with no creator)
 *    — cannot change the access level.
 * 4. Privileged roles (Firm Admin / Analyst / Fund Admin / Auditor — value < FULL_ACCESS_VIEWER_VALUE):
 *    - cannot change a Private version someone else created;
 *    - can change all other versions, including legacy rows with no creator
 *      (those can never be Private, so this carve-out never blocks them).
 * 5. All other roles (Limited Viewer, Export Viewer, Document Viewer) — cannot change.
 *
 * The backend is the true authority; this hook only drives disable/hide in the UI.
 */
export const useCapTableAccessLevelPermissions = ({
  captableInfo,
  currentFirmRole,
  currentUserId,
}: UseCapTableAccessLevelPermissionsParams): UseCapTableAccessLevelPermissionsReturn => {
  const canEditAccessLevel = useMemo(() => {
    if (!captableInfo || currentFirmRole == null || currentUserId == null) {
      return false;
    }

    if (captableInfo.is_primary) {
      return false;
    }

    const isCreator = captableInfo.created_by != null && captableInfo.created_by === currentUserId;
    if (isCreator) {
      return true;
    }

    if (currentFirmRole === FULL_ACCESS_VIEWER_VALUE) {
      return false;
    }

    // Privileged roles (value < FULL_ACCESS_VIEWER_VALUE): Firm Admin (0), Analyst (2), Fund Admin (3), Auditor (4).
    // Company User (1) also falls here but cannot reach cap table pages in practice.
    if (currentFirmRole < FULL_ACCESS_VIEWER_VALUE) {
      // Cannot change a Private version owned by someone else.
      if (captableInfo.access_level === CapTableDetailsModel.access_level.PRIVATE) {
        return false;
      }
      return true;
    }

    return false;
  }, [captableInfo, currentFirmRole, currentUserId]);

  return { canEditAccessLevel };
};
